// File: frontend/src/pages/VenuePage.tsx
//
// fantasy.futbol/<bar>: a partner bar's own weekend contest
// (BAR-CONTESTS-SCOPE-2026-10-06.md). Unlisted; reached by URL or the bar's
// QR code. Branded as the bar while open. What it shows follows the contest:
//   entry - the builder (after the bar's entry code), with the latest result
//           on top until the next contest's first kickoff
//   live  - the Scorecard while the matches play
//   final - the result, until next weekend takes over
//   none  - a holding screen
// The builder and Scorecard render in place, so the bar's URL stays.

import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format, isValid, parseISO } from 'date-fns';
import SEO from '@/components/common/SEO';
import Spinner from '@/components/common/Spinner';
import { useAuth } from '@/contexts/AuthContext';
import { useVenueBrand } from '@/contexts/VenueBrandContext';
import { checkVenueCode, fetchVenueContest, VenueContestResponse } from '@/api/venues';
import { fetchContestRankings } from '@/api/dfs';
import { recordReferralScan } from '@/api/referrals';
import { saveReferral } from '@/utils/referral';
import { readVenueCode, saveVenueCode, venueTheme } from '@/utils/venues';
import { mixpanelTrack } from '@/services/mixpanel';
import { isNativePlatform } from '@/services/platform';
import { userFacingError } from '@/utils/userFacingError';
import DfsLineupBuilderPage from '@/pages/DfsLineupBuilderPage';
import DfsContestPage from '@/pages/DfsContestPage';

const formatKickoff = (iso: string | null): string | null => {
  if (!iso) return null;
  const date = parseISO(iso);
  return isValid(date) ? format(date, "EEE MMM d, h:mm a") : null;
};

const PrizeCard: React.FC<{
  data: VenueContestResponse;
  leaderboardContestId: number | null;
}> = ({ data, leaderboardContestId }) => (
  <section className="mx-auto mb-3 w-full max-w-5xl rounded-xl border border-accent-yellow/30 bg-primary-dark px-4 py-3" aria-label={`${data.venue.name} contest`}>
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
      <h1 className="font-heading text-lg font-bold text-accent-yellow">{data.venue.name} Weekend Contest</h1>
      <div className="flex items-center gap-3 text-xs">
        {leaderboardContestId != null && (
          <Link to={`/dfs/contest/${leaderboardContestId}`} className="font-bold text-accent-light-blue hover:underline">Leaderboard</Link>
        )}
      </div>
    </div>
    <p className="mt-1 text-sm text-gray-300">
      Top score wins a {data.venue.prize_text}, collected in person at {data.venue.name}.
    </p>
    {isNativePlatform() && (
      <p className="mt-1 text-[11px] text-gray-500">Apple is not a sponsor of, and is not involved in, this contest.</p>
    )}
  </section>
);

const CodeGate: React.FC<{ slug: string; venueName: string; onAccepted: () => void }> = ({ slug, venueName, onAccepted }) => {
  const [code, setCode] = useState('');
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!code.trim() || checking) return;
    setChecking(true);
    setError(null);
    try {
      await checkVenueCode(slug, code.trim());
      saveVenueCode(slug, code.trim());
      mixpanelTrack('Venue Code Entered', { 'Venue': slug, 'Success': true });
      onAccepted();
    } catch (err) {
      mixpanelTrack('Venue Code Entered', { 'Venue': slug, 'Success': false });
      setError(userFacingError(err, "We couldn't check the code. Try again in a moment.", { section: 'venue_code' }));
    } finally {
      setChecking(false);
    }
  };

  return (
    <form onSubmit={submit} className="mx-auto w-full max-w-md rounded-xl border border-gray-700 bg-surface-card p-5">
      <h2 className="font-heading text-lg font-bold text-white">Enter the contest code</h2>
      <p className="mt-1 text-sm text-gray-300">Ask the staff at {venueName} for this weekend's code.</p>
      <label htmlFor="venue-code" className="sr-only">Contest code</label>
      <input
        id="venue-code"
        value={code}
        onChange={e => setCode(e.target.value)}
        autoComplete="off"
        autoCapitalize="none"
        className="mt-4 min-h-11 w-full rounded-lg border border-gray-600 bg-primary-dark px-3 text-base text-white placeholder-gray-500 focus:border-accent-yellow focus:outline-none"
        placeholder="Contest code"
        maxLength={64}
      />
      {error && <p className="mt-2 text-sm text-red-400" role="alert">{error}</p>}
      <button
        type="submit"
        disabled={!code.trim() || checking}
        className="mt-4 min-h-11 w-full rounded-lg bg-accent-yellow font-heading text-base font-bold text-primary transition-opacity disabled:opacity-50"
      >
        {checking ? 'Checking...' : 'Continue'}
      </button>
    </form>
  );
};

/** The latest result above next weekend's builder, until its first kickoff. */
const LastResultsCard: React.FC<{ contestId: number }> = ({ contestId }) => {
  const { token } = useAuth();
  const { data } = useQuery({
    queryKey: ['venueLastResults', contestId],
    queryFn: () => fetchContestRankings(contestId, token ?? null),
    staleTime: 10 * 60_000,
  });
  const top = (data?.rankings ?? []).filter(r => r.rank != null).slice(0, 3);
  if (!top.length) return null;
  return (
    <section className="mx-auto mb-3 w-full max-w-5xl rounded-xl border border-gray-700 bg-surface-card px-4 py-3" aria-label="Last contest's results">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="font-heading text-sm font-bold uppercase tracking-wide text-accent-yellow">Last contest's results</h2>
        <Link to={`/dfs/contest/${contestId}`} className="text-xs font-bold text-accent-light-blue hover:underline">Full results</Link>
      </div>
      <ol className="mt-2 grid gap-1 text-sm">
        {top.map(entry => (
          <li key={entry.lineup_id} className="flex items-baseline justify-between gap-3">
            <span className="min-w-0 truncate text-white"><span className="mr-2 font-heading font-bold text-accent-yellow">{entry.rank}</span>{entry.username ?? 'Player'}</span>
            <span className="font-heading font-bold tabular-nums text-white">{entry.score.toFixed(1)}</span>
          </li>
        ))}
      </ol>
    </section>
  );
};

const VenuePage: React.FC<{ slug: string }> = ({ slug }) => {
  const theme = venueTheme(slug);
  useVenueBrand(slug);
  const [hasCode, setHasCode] = useState(() => !!readVenueCode(slug));

  // The QR scan: attribution + scan count, once per visit (as /bar/<slug> did).
  const recorded = useRef(false);
  useEffect(() => {
    if (recorded.current) return;
    recorded.current = true;
    saveReferral(slug);
    recordReferralScan(slug);
    mixpanelTrack('Bar QR Scan', { 'Ref': slug, 'Venue': slug });
  }, [slug]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['venueContest', slug],
    queryFn: () => fetchVenueContest(slug),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });

  const name = data?.venue.name ?? theme?.name ?? 'Partner bar';
  const seo = <SEO title={`${name} | Fantasy Futbol`} description={`${name}'s weekend fantasy soccer contest.`} path={`/${slug}`} noindex />;

  if (isLoading) {
    return <>{seo}<div className="flex min-h-[50vh] items-center justify-center"><Spinner /></div></>;
  }
  if (error || !data) {
    return (
      <>{seo}
        <div className="mx-auto max-w-md p-6 text-center text-sm text-gray-300">
          {userFacingError(error, "This contest couldn't be loaded. Try again in a moment.", { section: 'venue_page' })}
        </div>
      </>
    );
  }

  const needsCode = data.phase === 'entry' && data.venue.requires_code && !hasCode;
  // No "Change code" for the demo (owner 2026-10-06): too confusing for now.
  const prize = (
    <PrizeCard
      data={data}
      leaderboardContestId={data.phase === 'entry' && !needsCode ? data.contest_id : null}
    />
  );

  if (data.phase === 'none' || data.contest_id == null) {
    const next = formatKickoff(data.next_start_time);
    return (
      <>{seo}
        <div className="px-1 pt-2">
          {prize}
          <div className="mx-auto max-w-md rounded-xl border border-gray-700 bg-surface-card p-6 text-center">
            <p className="font-heading text-lg font-bold text-white">{next ? `The next contest starts ${next}.` : "The next contest hasn't been scheduled yet."}</p>
          </div>
        </div>
      </>
    );
  }

  if (data.phase === 'entry') {
    return (
      <>{seo}
        <div className="pt-2">
          <div className="px-1">
            {prize}
            {data.previous_contest_id != null && <LastResultsCard contestId={data.previous_contest_id} />}
          </div>
          {needsCode
            ? <div className="px-1"><CodeGate slug={slug} venueName={data.venue.name} onAccepted={() => setHasCode(true)} /></div>
            : <DfsLineupBuilderPage key={data.contest_id} contestIdOverride={String(data.contest_id)} />}
        </div>
      </>
    );
  }

  // live or final: the Scorecard (the user's lineup and the leaderboard).
  return (
    <>{seo}
      <div className="pt-2">
        <div className="px-1">{prize}</div>
        <DfsContestPage key={data.contest_id} contestIdOverride={String(data.contest_id)} />
      </div>
    </>
  );
};

export default VenuePage;
